// The acreage a parcel read path reports (#1546).
//
// `GET /api/v1/parcels` and `GET /api/v1/parcels/{id}` report the
// acreage of the tract the listing actually OFFERS, not the acreage of
// the parcel the listing happens to be attached to, so the two read
// paths agree with the Big & Cheap screen. A 1-acre unit inside a
// 640-acre parcel is a 1-acre offer; showing 640 next to its price is
// what #1540 was about.
//
// The response carries both numbers and the provenance of the one it
// reports:
//
//   area_acres            the OFFERED acreage, or null when the offer
//                          could not be resolved. It is never the
//                          parent's number wearing the offer's label.
//   parcel_area_acres     the attached PARCEL's own acreage, always
//                          reported so both numbers stay visible.
//   offered_acreage_source where the reported number came from.
//   acreage_conflict      why the offer and the parcel disagree, or
//                          null when they agree.
//   acreage_conflict_reason the resolver's explanation.
//   acreage_verified      true when the reported number can be trusted.
//   acreage_corroborated  true when the listing's own address states an
//                          acreage that agrees with the stated lot size.
//
// A null `area_acres` is a real state, not a missing value: the offered
// tract's size is unknown, and the UI has to say so rather than fall
// back to the parent.

/** What the reported acreage is based on. */
export const ACREAGE_SOURCE_LABELS = {
  listing_lot: "Listing's stated lot size",
  parcel_geometry: 'Parcel geometry',
  unresolved: 'Unresolved',
};

/** Why the offer and the parcel it is attached to disagree. */
export const ACREAGE_CONFLICT_LABELS = {
  offered_smaller_than_parcel: 'The offer is a fraction of the parcel',
  offered_larger_than_parcel: 'The offer covers more than the parcel',
  offered_implausible: 'The offer cannot be reconciled with the parcel',
  listing_text_subdivision: 'The address names a subdivision of the parcel',
  offered_unavailable: "The listing states no lot size",
};

/**
 * Read the acreage provenance off a parcel detail or summary.
 *
 * @param {object|null} parcel - a ParcelDetail or ParcelSummary
 * @returns {{
 *   acres: number|null,
 *   parcelAcres: number|null,
 *   source: string|null,
 *   conflict: string|null,
 *   conflictLabel: string|null,
 *   conflictReason: string|null,
 *   unresolved: boolean,
 *   unknown: boolean,
 *   verified: boolean,
 *   corroborated: boolean,
 *   hasConflict: boolean,
 * }}
 */
export function offeredAcreageInfo(parcel) {
  const acres = parcel?.area_acres ?? null;
  const parcelAcres = parcel?.parcel_area_acres ?? null;
  const conflict = parcel?.acreage_conflict ?? null;
  const source = parcel?.offered_acreage_source ?? null;
  // "Unresolved" means there IS an offer and its acreage could not be
  // resolved. A parcel with no acreage at all is a different thing and
  // is not an offer that failed to resolve.
  const unresolved = acres == null && source === 'unresolved';
  return {
    acres,
    parcelAcres,
    source,
    conflict,
    conflictLabel: conflict ? (ACREAGE_CONFLICT_LABELS[conflict] || conflict) : null,
    conflictReason: parcel?.acreage_conflict_reason ?? null,
    unresolved,
    // No acreage anywhere, offered or parcel.
    unknown: acres == null && !unresolved,
    verified: parcel?.acreage_verified === true,
    corroborated: parcel?.acreage_corroborated === true,
    hasConflict: conflict != null,
  };
}

/**
 * Format an acreage for display, or say that it is unresolved.
 *
 * @param {object|null} parcel - a ParcelDetail or ParcelSummary
 * @param {number} [digits=2]
 * @returns {string}
 */
export function formatOfferedAcres(parcel, digits = 2) {
  const info = offeredAcreageInfo(parcel);
  if (info.unresolved) return 'Unresolved';
  if (info.acres == null) return 'Not available';
  return `${info.acres.toFixed(digits)} ac`;
}

/**
 * Format the reported acreage together with the parcel's own acreage
 * when the two differ.
 *
 * "4.00 ac (parcel is 40.00 ac)" says both things a buyer needs: the
 * size of the tract on offer, and the size of the parcel it was
 * matched to. When the offer could not be resolved the reported
 * acreage is "Unresolved" and the parcel's acreage is the only number
 * on offer, so it is shown as a reference rather than as the answer.
 *
 * @param {object|null} parcel - a ParcelDetail or ParcelSummary
 * @param {number} [digits=2]
 * @returns {string}
 */
export function formatOfferedAcresWithParent(parcel, digits = 2) {
  const info = offeredAcreageInfo(parcel);
  const offered =
    info.acres == null
      ? (info.unresolved ? 'Unresolved' : 'Not available')
      : `${info.acres.toFixed(digits)} ac`;
  if (info.parcelAcres == null) return offered;
  const same =
    info.acres != null && Math.abs(info.acres - info.parcelAcres) < 0.005;
  if (same) return offered;
  return `${offered} (parcel is ${info.parcelAcres.toFixed(digits)} ac)`;
}

/**
 * The reference note to show beside a reported acreage on a list or map
 * surface (#1546).
 *
 * `/parcels/table`, `/parcels/points` and `/parcels/boundaries` report the
 * OFFERED tract's acreage, the same number the detail page does, so the
 * parcel a listing is attached to can be a different size. The surface has
 * room for a tooltip, not for "4.00 ac (parcel is 40.00 ac)", so this
 * returns the sentence for the tooltip or undefined when there is nothing
 * to add. Safe on a payload that carries no provenance at all (an older
 * response, a row of a surface that does not band): it is undefined there.
 *
 * @param {object|null} parcel - a table row, a map point or a ParcelDetail
 * @returns {string|undefined}
 */
export function offeredAcresNote(parcel) {
  const info = offeredAcreageInfo(parcel);
  const parentText =
    info.parcelAcres != null
      ? `${Number(info.parcelAcres).toLocaleString(undefined, { maximumFractionDigits: 2 })} ac`
      : null;
  if (info.unresolved) {
    return parentText
      ? `The offered acreage could not be resolved; the attached parcel is ${parentText}`
      : 'The offered acreage could not be resolved';
  }
  if (
    info.acres != null &&
    info.parcelAcres != null &&
    Math.abs(info.acres - info.parcelAcres) >= 0.005
  ) {
    return `The offered tract; the attached parcel is ${parentText}`;
  }
  return undefined;
}

/**
 * The price per acre to show for a listing.
 *
 * It is the price divided by the acreage OF THE OFFER, which is the
 * number the price was asked for. When the offer's acreage could not
 * be resolved there is no per-acre figure: dividing the price by the
 * parent's acreage is the mistake this whole change exists to stop.
 *
 * @param {object|null} parcel - a ParcelDetail or ParcelSummary
 * @returns {number|null} dollars per acre, or null
 */
export function offeredPricePerAcre(parcel) {
  const info = offeredAcreageInfo(parcel);
  const price = parcel?.listing_price ?? null;
  if (price == null || price <= 0) return null;
  if (info.acres == null || info.acres <= 0) return null;
  return price / info.acres;
}

/**
 * The acreage a PARCEL-level figure should be divided by.
 *
 * Some figures describe the parcel as a holding (the county's value
 * estimate, an assessed value), not the tract on offer. Those keep
 * using the parcel's own acreage, which the response reports
 * separately, so a per-parcel estimate is not silently divided by a
 * sub-lot's size.
 *
 * @param {object|null} parcel - a ParcelDetail or ParcelSummary
 * @returns {number|null} acres
 */
export function parcelLevelAcres(parcel) {
  const info = offeredAcreageInfo(parcel);
  if (info.parcelAcres != null) return info.parcelAcres;
  // A payload with no parcel_area_acres (an older response, or an
  // endpoint that does not band) falls back to the reported number.
  return info.acres;
}
